import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { ChartArea } from "../utils/coordinateConversion";
import {
	DEFAULT_TIME_ZONE_MODE,
	type TimeZoneMode,
	formatDateTime,
} from "../utils/timeZone";

const HANDLE_SIZE = 14;
const LINE_WIDTH = 2;

interface DraggableLineControlV2Props {
	/** The chart's pixel boundaries */
	chartArea: ChartArea;
	/** Initial pixel X position for the cursor */
	initialLeft: number;
	/** Callback when drag ends with new pixel position */
	onDragEnd: (newLeft: number) => void;
	/** Optional: Callback during dragging for real-time updates */
	onChange?: (newLeft: number) => void;
	/** Color of the cursor line (e.g., 'red', 'blue', '#ff0000') */
	color: string;
	/** Optional: X position of the other cursor to prevent overlap */
	otherLineCoord?: number;
	isStartCursor: boolean;
	/** Minimum spacing between cursors in pixels */
	minSpacing?: number;
	/** Optional: Timestamp to display at the bottom of the cursor */
	timestamp?: number;
	/** Timezone used to render the timestamp label */
	timeZoneMode?: TimeZoneMode;
}

/**
 * External draggable cursor control for the trending chart
 * Renders a draggable pin handle with a vertical line that can be positioned over the chart
 */
const DraggableLineControlV2: React.FC<DraggableLineControlV2Props> =
	React.memo(
		({
			initialLeft,
			otherLineCoord,
			isStartCursor,
			onDragEnd,
			onChange,
			color,
			chartArea,
			minSpacing = HANDLE_SIZE,
			timestamp,
			timeZoneMode = DEFAULT_TIME_ZONE_MODE,
		}) => {
			const [isDragging, setIsDragging] = useState(false);
			const [left, setLeft] = useState(initialLeft);
			const ref = useRef<HTMLDivElement>(null);
			const labelRef = useRef<HTMLDivElement>(null);
			const dragStartPositionRef = useRef<number>(initialLeft);
			const [labelShift, setLabelShift] = useState(0);

			// Update local position when initialLeft prop changes
			useEffect(() => {
				setLeft(initialLeft);
			}, [initialLeft]);

			useLayoutEffect(() => {
				const node = labelRef.current;
				if (!node) {
					return;
				}

				const halfWidth = node.offsetWidth / 2;
				const min = chartArea.left + halfWidth;
				const max = chartArea.right - halfWidth;

				setLabelShift(Math.min(Math.max(left, min), max) - left);
			}, [left, chartArea, timestamp, timeZoneMode]);

			const handleMouseDown = useCallback(
				(e: React.MouseEvent<HTMLDivElement>) => {
					setIsDragging(true);
					dragStartPositionRef.current = left;
					e.stopPropagation();
					e.preventDefault();
				},
				[left],
			);

			const handleMouseMove = useCallback(
				(e: MouseEvent) => {
					if (!isDragging || !ref.current || !ref.current.offsetParent) return;

					// Calculate new position relative to the chart container
					const containerRect =
						ref.current.offsetParent.getBoundingClientRect();
					let newLeft = e.clientX - containerRect.left;

					// Clamp to chart area boundaries
					newLeft = Math.max(
						chartArea.left,
						Math.min(newLeft, chartArea.right),
					);

					// Prevent cursor overlap if otherLineCoord is provided
					if (otherLineCoord !== undefined) {
						newLeft = isStartCursor
							? Math.min(newLeft, otherLineCoord - minSpacing)
							: Math.max(newLeft, otherLineCoord + minSpacing);
					}

					setLeft(newLeft);

					// Call onChange callback for real-time updates during drag
					if (onChange) {
						onChange(newLeft);
					}
				},
				[
					isDragging,
					chartArea,
					otherLineCoord,
					isStartCursor,
					minSpacing,
					onChange,
				],
			);

			const handleMouseUp = useCallback(() => {
				if (isDragging) {
					setIsDragging(false);

					// Only trigger callback if position actually changed
					if (left !== dragStartPositionRef.current) {
						onDragEnd(left);
					}
				}
			}, [isDragging, left, onDragEnd]);

			useEffect(() => {
				if (isDragging) {
					window.addEventListener("mousemove", handleMouseMove);
					window.addEventListener("mouseup", handleMouseUp);

					return () => {
						window.removeEventListener("mousemove", handleMouseMove);
						window.removeEventListener("mouseup", handleMouseUp);
					};
				}
				return undefined;
			}, [isDragging, handleMouseMove, handleMouseUp]);

			const chartHeight = chartArea.bottom - chartArea.top;

			// Format timestamp for display
			const formatTimestamp = (ts: number | undefined): string =>
				ts ? formatDateTime(ts, timeZoneMode) : "";

			return (
				<div
					ref={ref}
					onMouseDown={handleMouseDown}
					style={{
						position: "absolute",
						top: chartArea.top - HANDLE_SIZE,
						left: left - HANDLE_SIZE / 2,
						width: HANDLE_SIZE,
						height: HANDLE_SIZE,
						backgroundColor: color,
						borderRadius: "50%",
						cursor: isDragging ? "grabbing" : "grab",
						zIndex: 1000,
						boxShadow: isDragging
							? "0 4px 8px rgba(0, 0, 0, 0.3)"
							: "0 2px 4px rgba(0, 0, 0, 0.2)",
						transition: isDragging ? "none" : "box-shadow 0.2s ease",
						userSelect: "none",
					}}
				>
					{/* Vertical line extending from the pin */}
					<div
						style={{
							position: "absolute",
							top: HANDLE_SIZE,
							left: (HANDLE_SIZE - LINE_WIDTH) / 2,
							width: LINE_WIDTH,
							height: chartHeight,
							backgroundColor: color,
							pointerEvents: "none",
							opacity: isDragging ? 0.8 : 1,
						}}
					/>
					{/* Timestamp label at the bottom */}
					{timestamp && (
						<div
							ref={labelRef}
							style={{
								position: "absolute",
								top: HANDLE_SIZE + chartHeight + 4,
								left: "50%",
								transform: `translateX(calc(-50% + ${labelShift}px))`,
								backgroundColor: "rgba(255, 255, 255, 0.95)",
								color: color,
								padding: "4px 8px",
								borderRadius: "4px",
								fontSize: "11px",
								fontWeight: "bold",
								whiteSpace: "nowrap",
								boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
								border: `2px solid ${color}`,
								pointerEvents: "none",
								zIndex: 1001,
							}}
						>
							{formatTimestamp(timestamp)}
						</div>
					)}
				</div>
			);
		},
	);

DraggableLineControlV2.displayName = "DraggableLineControlV2";

export default DraggableLineControlV2;
